import { Suspense, useMemo } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import 'dayjs/locale/cs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/nl';
import 'dayjs/locale/pl';
import 'dayjs/locale/pt';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { useAppUserStore, useOptimisticSignUpStore } from '@data/storage';
import { Language } from '@data/utils/enums';
import { initPulseLifecycleFlush } from '@data/utils/pulse';
import { PrototypeModeIndicator } from '@prototypes/shared/components/PrototypeModeIndicator';
import { AntdConfigProvider, getConfigTheme, Spinner, useTheme } from '@ui';
import { antdLocales } from './constants';
import './i18n';
import { router } from './router';
import { ErrorBoundary, queryClient, sentryInit, SEO } from './utils';
import './styles/global.css';

const container: HTMLElement = document.getElementById('root') as HTMLElement;

sentryInit();
initPulseLifecycleFlush();

const App = () => {
  const { bgColor } = useTheme();
  const { t, i18n } = useTranslation();
  const { isLoading } = useOptimisticSignUpStore();
  const currentLocale = (i18n.language as Language) || Language.EN;

  const configTheme = useMemo(() => getConfigTheme(bgColor), [bgColor]);

  const user = useAppUserStore((state) => state.user);

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AntdConfigProvider locale={antdLocales[currentLocale]} theme={configTheme}>
            <Suspense fallback={<Spinner takeWholePage />}>
              <SEO title={t('seo.title')} description={t('seo.description')} />
              <RouterProvider router={router(isLoading, user?.currentRole)} />
              <PrototypeModeIndicator />
              {/* Hidden under browser automation: the floating button sits over drawer footers
                  in the e2e viewport and intercepts their clicks. */}
              {!navigator.webdriver && <ReactQueryDevtools initialIsOpen={false} />}
            </Suspense>
          </AntdConfigProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </GoogleOAuthProvider>
  );
};

const root = createRoot(container!);
root.render(<App />);
